import {
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Theme,
  WithStyles,
  createStyles,
  withStyles
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { Close, Redo } from '@material-ui/icons';
import autobind from 'autobind-decorator';
import * as React from 'react';
import { FormButton, FormDialog } from 'src/components';
import { SnackbarVariant } from 'src/components/AppSnackbar';
import {
  CurrentPersonaValue,
  withCurrentPersona
} from 'src/contexts/CurrentPersonaContext';
import { API } from 'src/definitions';
import AxiosException from 'src/exceptions/AxiosException';
import AddStepRequest from 'src/schemas/AddStepRequest';
import { load } from 'src/services/Loader';
import RelmApi from 'src/services/RelmApi';
import { toast } from 'src/services/Toaster';
import ValidationTrait, {
  ValidationTraitState
} from 'src/services/ValidationTrait';
import { buildTrelloLaneFromStep } from 'src/utilities';

// region component styles
const styles = (theme: Theme) =>
  createStyles({
    paper: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        maxWidth: `${theme.breakpoints.values.sm}px`
      }
    },
    typographyTitle: {
      textAlign: 'center'
    },
    cardHeader: {
      paddingTop: '10px',
      paddingBottom: '0'
    },
    cardContent: {
      paddingTop: '0',
      paddingBottom: '0'
    },
    cardActions: {
      paddingTop: '0',
      paddingBottom: '20px'
    }
  });

// endregion
// region component props
interface ExternalProps {
  classes?: Partial<ClassNameMap<keyof typeof styles>>;

  onStepAdded?: () => void;
}

type InternalProps = Required<ExternalProps>;

type Props = InternalProps &
  CurrentPersonaValue &
  ReactTrello.NewLaneTemplateProps &
  WithStyles<typeof styles>;

interface State extends ValidationTraitState<API.Steps.AddStep.Request> {}

// endregion

/**
 *
 */
@withCurrentPersona<Props>()
class AddStepLaneTemplate extends React.Component<Props, State> {
  static readonly defaultProps = {
    currentPersona: null,
    onCurrentPersonaChange: () => {},
    onStepAdded: undefined,
    onCancel: () => {},
    onAdd: () => {}
  };

  private readonly _validator = new ValidationTrait(this, AddStepRequest);

  readonly state: State = {
    inputValues: {
      name: ''
    },
    inputErrors: {
      name: ''
    }
  };

  async tryAddNewStep() {
    try {
      const newStep = (
        await RelmApi.createStep(
          this.props.currentPersona!.id,
          this.state.inputValues
        )
      ).data;

      toast(SnackbarVariant.SUCCESS, 'Successfully created step');

      this.props.onAdd(buildTrelloLaneFromStep(newStep, 'ignore'));
      this.props.onStepAdded && this.props.onStepAdded();
    } catch (error) {
      if (error instanceof AxiosException) {
        toast(
          SnackbarVariant.ERROR,
          'Something went wrong when trying to add step'
        );

        return;
      }

      this._validator.handlePossibleValidationError(error);
    }
  }

  // region autobound methods
  @autobind
  handleAddStepButtonClick() {
    load(this.tryAddNewStep());
  }

  @autobind
  handleCloseClick() {
    this.props.onCancel();
  }

  // endregion
  // region render & get-render-content methods
  render() {
    return (
      <FormDialog classes={{ paper: this.props.classes.paper }} open>
        <CardHeader
          className={this.props.classes.cardHeader}
          avatar={<Redo />}
          action={
            <IconButton onClick={this.handleCloseClick}>
              <Close />
            </IconButton>
          }
          titleTypographyProps={{
            className: this.props.classes.typographyTitle
          }}
          title="Add a Step"
        />
        <CardContent className={this.props.classes.cardContent}>
          {this._validator.buildTextFieldForProperty('name', 'Name', {
            autoFocus: true
          })}
        </CardContent>
        <CardActions className={this.props.classes.cardActions}>
          <FormButton
            variant="outlined"
            fullWidth
            onClick={this.handleAddStepButtonClick}
          >
            Add Step
          </FormButton>
        </CardActions>
      </FormDialog>
    );
  }

  // endregion
}

export default withStyles(styles)(AddStepLaneTemplate);
